import { useState } from 'react';
import { NavLink, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { type BudgetStand } from '@beholder/shared/module/budget';
import type { ModulSeiteProps } from '../../core/modul';
import { Knopf, Laden, Leer, Meldung, datum, datumZeit, useAktion } from '../../core/ui';
import { api } from '../../core/api';
import { useLiveVerbunden } from '../../core/live';
import { Auswertung } from './Auswertung';
import { ART_NAME, BelegDialog, StatusMarke, type DialogArt } from './BelegDialog';
import { Euro } from './Euro';
import { Belege } from './Belege';
import { pfad, useBudget, useMindmapKnoten, useMitglieder } from './hilfe';
import { Uebersicht } from './Uebersicht';
import './stil.css';

export function Seite({ projekt, ich, darfSchreiben }: ModulSeiteProps) {
  const { daten: s, fehler, neuLaden } = useBudget(projekt.id);
  const { daten: mitglieder } = useMitglieder(projekt.id);
  const { daten: mm } = useMindmapKnoten(projekt.id);
  const [such, setSuch] = useSearchParams();
  const verbunden = useLiveVerbunden();
  const basis = `/p/${projekt.id}/budget`;

  // Geöffneter Beleg steht in der Adresse (?beleg=…), damit Zurück und Teilen funktionieren
  const belegId = such.get('beleg');
  const neu = such.get('neu');
  const modus: DialogArt | null = belegId
    ? s?.vorschlaege.some((v) => v.id === belegId)
      ? { art: 'vorschlag', id: belegId }
      : { art: 'ansicht', id: belegId }
    : neu === 'rechnung' || neu === 'angebot' || neu === 'einnahme'
      ? { art: 'neu', belegArt: neu }
      : null;
  const oeffnen = (m: DialogArt | null) => {
    const n = new URLSearchParams(such);
    n.delete('beleg');
    n.delete('neu');
    if (m?.art === 'neu') n.set('neu', m.belegArt);
    else if (m) n.set('beleg', m.id);
    setSuch(n);
  };

  if (fehler && !s) return <div className="seite"><Meldung>{fehler}</Meldung></div>;
  if (!s) return <Laden />;

  return (
    <div className="seite bu-seite">
      <header className="bu-kopf">
        <nav className="bu-unterreiter" aria-label="Budget">
          <NavLink end to={basis}>Übersicht</NavLink>
          <NavLink to={`${basis}/belege`}>Belege</NavLink>
          {(s.vorschlaege.length > 0 || darfSchreiben) && (
            <NavLink to={`${basis}/vorschlaege`}>
              Vorschläge{s.vorschlaege.length > 0 && <span className="bu-zahl">{s.vorschlaege.length}</span>}
            </NavLink>
          )}
          <NavLink to={`${basis}/auswertung`}>Auswertung</NavLink>
        </nav>
        <span className="abstand" />
        <span className="leise klein">
          Beträge in €, brutto
          {!verbunden && ' · Live-Verbindung getrennt'}
          {!darfSchreiben && ' · nur ansehen'}
        </span>
      </header>
      <Meldung>{fehler}</Meldung>
      <Routes>
        <Route index element={<Uebersicht projektId={projekt.id} s={s} darfSchreiben={darfSchreiben} darfLoeschen={darfSchreiben && projekt.verwalten} neuLaden={() => void neuLaden()} />} />
        <Route
          path="belege"
          element={<Belege projektId={projekt.id} s={s} mitglieder={mitglieder} darfSchreiben={darfSchreiben} oeffnen={oeffnen} neuLaden={() => void neuLaden()} />}
        />
        <Route path="vorschlaege" element={<Vorschlaege projektId={projekt.id} s={s} darfSchreiben={darfSchreiben} oeffnen={oeffnen} neuLaden={() => void neuLaden()} />} />
        <Route path="auswertung" element={<Auswertung projektId={projekt.id} s={s} />} />
        <Route path="*" element={<Navigate to={basis} replace />} />
      </Routes>
      {modus && (modus.art !== 'neu' || darfSchreiben) && (
        <BelegDialog
          projektId={projekt.id}
          s={s}
          modus={modus}
          ich={ich}
          mitglieder={mitglieder}
          knoten={mm?.knoten ?? []}
          darfSchreiben={darfSchreiben}
          darfLoeschen={darfSchreiben && projekt.verwalten}
          schliessen={() => oeffnen(null)}
          oeffnen={oeffnen}
          fertig={() => neuLaden()}
        />
      )}
    </div>
  );
}

const HERKUNFT = { mail: 'Mail', ki: 'KI-Hilfe', hand: 'von Hand' } as const;

function Vorschlaege({
  projektId,
  s,
  darfSchreiben,
  oeffnen,
  neuLaden,
}: {
  projektId: string;
  s: BudgetStand;
  darfSchreiben: boolean;
  oeffnen(m: DialogArt): void;
  neuLaden(): void;
}) {
  const a = useAktion();
  if (!s.vorschlaege.length) {
    return (
      <Leer titel="Keine offenen Vorschläge">
        Rechnungen, Angebote und Einnahmen, die die Mail-Auswertung oder die KI-Hilfe erkennt, erscheinen hier zur Bestätigung. Verworfene Vorschläge liegen 180 Tage im Papierkorb
        des Projekts.
      </Leer>
    );
  }
  async function schnell(id: string, was: 'uebernehmen' | 'verwerfen') {
    const r = await a.ausfuehren(() => api.post(pfad(projektId, `/belege/${id}/${was}`)));
    if (r) neuLaden();
  }
  return (
    <div className="stapel">
      <p className="leise bu-ohne-abstand">
        Diese Belege wurden automatisch erkannt und zählen erst, wenn jemand sie übernimmt. Vor dem Übernehmen lassen sie sich prüfen und anpassen.
      </p>
      <Meldung>{a.fehler}</Meldung>
      <ul className="bu-vorschlaege">
        {s.vorschlaege.map((v) => (
          <li key={v.id} className="karte">
            <div className="reihe">
              <span className="bu-marke bu-marke-akzent">{ART_NAME[v.art]}</span>
              <strong>{v.titel}</strong>
              <StatusMarke b={v} />
              <span className="abstand" />
              <strong>
                <Euro b={v} fokus />
              </strong>
            </div>
            <p className="leise klein bu-ohne-abstand">
              Herkunft: {HERKUNFT[v.quelleArt]} · erkannt {datumZeit(v.erstelltAm)}
              {v.absender && ` · von ${v.absender}`}
              {v.datum && ` · Belegdatum ${datum(v.datum)}`}
              {` · Kategorie: ${v.kategoriePfad ?? (v.kategorieVorschlag ? `„${v.kategorieVorschlag}“ (nicht gefunden)` : 'keine')}`}
              {v.anzahlAnlagen > 0 && ` · ${v.anzahlAnlagen} ${v.anzahlAnlagen === 1 ? 'Anlage' : 'Anlagen'}`}
            </p>
            <div className="reihe">
              <Knopf klein onClick={() => oeffnen({ art: 'vorschlag', id: v.id })}>
                Prüfen & anpassen
              </Knopf>
              {darfSchreiben && (
                <>
                  <Knopf klein art="haupt" onClick={() => schnell(v.id, 'uebernehmen')} disabled={a.laeuft}>
                    ✓ Übernehmen
                  </Knopf>
                  <Knopf
                    klein
                    art="gefahr"
                    onClick={() => confirm(`Vorschlag „${v.titel}“ verwerfen?\n\nEr liegt dann 180 Tage im Papierkorb und lässt sich dort zurückholen.`) && schnell(v.id, 'verwerfen')}
                    disabled={a.laeuft}
                  >
                    Verwerfen
                  </Knopf>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
